import { useEffect, useState } from "react";
import baseUrl from "../../api/baseUrl";
import PaymentButton from "../../components/reminder/PayButton";
import { useNavigate } from "react-router-dom";
import PaymentIcon from "@mui/icons-material/Payment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountPageScroll from "../../pages/myaccount/components/AccountPageScroll";
import { PAYHERE_RECURRING_ENABLED } from "../../config/payhere";

const initialStartDate = new Date();
const initialEndDate = new Date();
initialEndDate.setMonth(initialEndDate.getMonth() + 1);

function calcPendingMonths(paymentEndDate: Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(paymentEndDate);
  end.setHours(0, 0, 0, 0);
  if (end >= today) return 0;
  const diffDays = Math.ceil((today.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(1, Math.ceil(diffDays / 30));
}

function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

const RECURRING_BENEFITS = [
  "Automatic monthly renewal via PayHere",
  "Full access to clinic management tools",
  "Cancel anytime from your PayHere account",
];

const MONTHLY_BENEFITS = [
  "Pay monthly to keep your clinic active",
  "Full access to clinic management tools",
  "Renew each month before your plan expires",
];

const subscriptionBenefits = PAYHERE_RECURRING_ENABLED
  ? RECURRING_BENEFITS
  : MONTHLY_BENEFITS;

function PaymentData() {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const navigate = useNavigate();
  const [pendingMonths, setPendingMonths] = useState(0);

  const [formData, setformData] = useState({
    price: "",
    clinicName: "",
    title: "",
    lastName: "",
    firstName: "",
    email: "",
    address: "",
    orderId: "",
    phone: "",
    clinicid: "",
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  const monthlyPrice = Number(formData.price) || 0;
  // Cover only overdue months when pending; otherwise one month. No extra +1.
  const billingMonths = pendingMonths > 0 ? pendingMonths : 1;
  // PayHere first charge = amount + startup_fee; amount is always 1 month,
  // so startup is (pending - 1) months when catching up.
  const startupFee =
    pendingMonths > 1 ? monthlyPrice * (pendingMonths - 1) : 0;
  const paymentTotal = PAYHERE_RECURRING_ENABLED
    ? monthlyPrice + startupFee
    : monthlyPrice * billingMonths;
  const pendingBalance =
    pendingMonths > 0 ? monthlyPrice * pendingMonths : 0;

  const paymentFormData = {
    ...formData,
    monthlyPrice: String(monthlyPrice),
    pendingMonths,
  };

  useEffect(() => {
    baseUrl
      .get("/client/fee/getClinicPaymentbyId")
      .then((response) => {
        const emp = response?.data?.data;
        if (!emp) {
          console.warn("No payment data found.");
          return;
        }
        setformData((prevData) => {
          const start = new Date(emp.paymentendDate ?? new Date());
          const overdue = calcPendingMonths(start);
          setPendingMonths(overdue);
          return {
            ...prevData,
            price: emp?.price || "",
            clinicName: emp?.clinicName || "",
            firstName: emp?.firstName || "",
            lastName: emp?.lastName || "",
            title: emp?.title || "",
            address: emp?.address || "",
            email: emp?.email || "",
            orderId: emp?.orderId || "",
            phone: emp?.phone || "",
            clinicid: emp?.clinicid || "",
            startDate: start,
            endDate: addMonths(start, overdue > 0 ? overdue : 1),
          };
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const onNavigate = () => {
    navigate("/client/dashboard");
  };

  const logOut = () => {
    localStorage.clear();
    window.location.reload();
    window.location.href = "/login";
  };

  return (
    <AccountPageScroll className="bg-slate-50 dark:bg-slate-950">
      <div className="flex min-h-dvh w-full flex-col">
        <header className="bg-gradient-to-r from-primary to-primaryLight">
          <div className="mx-auto flex w-full max-w-6xl items-start justify-between gap-3 px-4 py-5 md:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/20">
                <PaymentIcon sx={{ fontSize: 28 }} className="text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-white md:text-2xl">Monthly Subscription</h1>
                <p className="text-sm text-white/80">
                  {PAYHERE_RECURRING_ENABLED
                    ? "Auto-renewed monthly via PayHere"
                    : "Pay monthly via PayHere"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={logOut}
              className="flex shrink-0 items-center gap-1 rounded-lg bg-white/15 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-white/25"
            >
              <LogoutIcon sx={{ fontSize: 16 }} />
              <span className="hidden sm:inline">Log Out</span>
            </button>
          </div>
        </header>

        <div className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between md:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 dark:bg-slate-800">
                <BusinessIcon sx={{ fontSize: 18 }} className="text-primary dark:text-primaryLight" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Clinic</p>
                <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {formData.clinicName || "—"}
                </p>
              </div>
            </div>
            <div className="sm:text-right">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Account holder</p>
              <p className="text-sm text-slate-700 dark:text-slate-200">
                {formData.title} {formData.firstName} {formData.lastName}
              </p>
            </div>
          </div>
        </div>

        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-5 md:px-8 md:py-6">
          <div className="grid gap-4 lg:grid-cols-12 lg:gap-6">
            <div className="space-y-4 lg:col-span-5">
              <div className="rounded-xl bg-gradient-to-br from-primary/10 to-primaryLight/5 p-4 dark:from-primary/15 dark:to-slate-900 md:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">Monthly rental</p>
                    <p className="mt-1 text-2xl font-bold text-primary dark:text-slate-100 md:text-3xl">
                      Rs. {formData.price || "0"}
                    </p>
                  </div>
                  {PAYHERE_RECURRING_ENABLED && (
                    <div className="flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 shadow-sm dark:bg-slate-800">
                      <AutorenewIcon sx={{ fontSize: 14 }} className="text-primary dark:text-primaryLight" />
                      <span className="text-[10px] font-semibold text-primary dark:text-primaryLight">Auto-renew</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900 md:p-5">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Subscription includes
                </p>
                <ul className="space-y-2">
                  {subscriptionBenefits.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: 16 }}
                        className="mt-0.5 shrink-0 text-primary dark:text-primaryLight"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {pendingMonths > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800/50 dark:bg-amber-900/20 md:p-5">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                    Pending balance: {pendingMonths} month{pendingMonths > 1 ? "s" : ""}
                  </p>
                  <p className="mt-1 text-xs text-amber-700 dark:text-amber-300/90">
                    {PAYHERE_RECURRING_ENABLED
                      ? `This payment covers ${pendingMonths} pending month${pendingMonths > 1 ? "s" : ""} (Rs. ${pendingBalance}). Auto-renew continues monthly after that.`
                      : `This payment covers ${pendingMonths} pending month${pendingMonths > 1 ? "s" : ""} (Rs. ${pendingBalance}).`}
                  </p>
                </div>
              )}
            </div>

            <div className="lg:col-span-7">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700 md:px-5">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">Payment summary</p>
                </div>
              
                <div className="divide-y divide-slate-200 dark:divide-slate-700">
                  <div className="flex items-center justify-between px-4 py-3 md:px-5">
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      {PAYHERE_RECURRING_ENABLED
                        ? "Monthly subscription"
                        : `Monthly rental (${billingMonths} mo)`}
                    </span>
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                      {PAYHERE_RECURRING_ENABLED ? `Rs. ${monthlyPrice}` : `Rs. ${paymentTotal}`}
                    </span>
                  </div>
                  {PAYHERE_RECURRING_ENABLED && startupFee > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 md:px-5">
                      <span className="text-sm text-slate-600 dark:text-slate-300">
                        Startup fee ({pendingMonths - 1} overdue mo)
                      </span>
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Rs. {startupFee}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between bg-slate-50 px-4 py-4 dark:bg-slate-800/60 md:px-5">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      {PAYHERE_RECURRING_ENABLED && pendingMonths > 0
                        ? "First payment total"
                        : "Total due"}
                    </span>
                    <span className="text-xl font-bold text-primary dark:text-slate-100">Rs. {paymentTotal}</span>
                  </div>
                </div>

                <div className="grid gap-0 border-t border-slate-200 sm:grid-cols-2 dark:border-slate-700">
                  <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 sm:border-b-0 sm:border-r dark:border-slate-700 md:px-5">
                    <CalendarTodayIcon sx={{ fontSize: 16 }} className="shrink-0 text-slate-500 dark:text-slate-400" />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Coverage from</p>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                        {formData.startDate.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-3 md:px-5">
                    <CalendarTodayIcon sx={{ fontSize: 16 }} className="shrink-0 text-slate-500 dark:text-slate-400" />
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Valid till</p>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                        {formData.endDate.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {PAYHERE_RECURRING_ENABLED ? (
                  <div className="flex items-start gap-2 border-t border-slate-200 bg-primary/5 px-4 py-3 dark:border-slate-700 dark:bg-primary/10 md:px-5">
                    <AutorenewIcon sx={{ fontSize: 16 }} className="mt-0.5 shrink-0 text-primary dark:text-primaryLight" />
                    <p className="min-w-0 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                      Then Rs. {monthlyPrice}/month — billed automatically each month
                    </p>
                  </div>
                ) : (
                  <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-700 md:px-5">
                    <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                      This is a one-time payment for {billingMonths} month{billingMonths > 1 ? "s" : ""}. Renew again before expiry to stay active.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <footer className="mt-auto border-t border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:px-8">
            {PAYHERE_RECURRING_ENABLED && (
              <p className="text-[10px] leading-snug text-slate-400 dark:text-slate-500 sm:text-right">
                We will store your card details when you subscribe.
              </p>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={onNavigate}
                className="h-10 w-full rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 sm:w-auto"
              >
                Cancel
              </button>

              <div className="w-full sm:w-auto [&>button]:flex [&>button]:h-10 [&>button]:w-full [&>button]:items-center [&>button]:justify-center [&>button]:px-5 [&>button]:text-sm sm:[&>button]:w-auto">
                <PaymentButton formData={paymentFormData} />
              </div>
            </div>
          </div>
        </footer>
      </div>
    </AccountPageScroll>
  );
}

export default PaymentData;
